import Papa from 'papaparse';
import { differenceInYears, addYears, addDays, format } from 'date-fns';
import { safeParseISO } from './financialCalculations.js';

// Define the paths to the life expectancy tables relative to the public folder
const CANADA_LIFE_EXPECTANCY_PROVINCIAL_PATH = '/data/canada/lifeexpectancy/2021_Life_Expectancy_by_Province__Sex__and_Age.csv';
const US_LIFE_EXPECTANCY_NORMALIZED_PATH = '/data/us/lifeexpectancy/us_life_expectancy_normalized.csv';

const LIFE_EXPECTANCY_TABLE_PATHS = {
    US: {
        stateLevel: US_LIFE_EXPECTANCY_NORMALIZED_PATH,
        nationalMale: '/data/us/lifeexpectancy/Table2.csv',    // Old national data for fallback
        nationalFemale: '/data/us/lifeexpectancy/Table3.csv',  // Old national data for fallback
        nationalDefault: '/data/us/lifeexpectancy/Table1.csv' // Old national data for fallback
    },
    Canada: CANADA_LIFE_EXPECTANCY_PROVINCIAL_PATH, // Single path for new Canadian data
};

// Column indices for the Canadian Provincial CSV
const CAN_PROV_PROVINCE_COL = 2;
const CAN_PROV_SEX_COL = 3;
const CAN_PROV_AGE_GROUP_COL = 4;
const CAN_PROV_LIFE_EXPECTANCY_COL = 5;

// Column indices for the new US Normalized CSV
const US_NORM_STATE_COL = 0;
const US_NORM_SEX_COL = 1;
const US_NORM_AGE_COL = 2;
const US_NORM_LIFE_EXPECTANCY_COL = 3;


// Mapping for US state names to 2-letter abbreviations
const US_STATE_NAME_TO_ABBREVIATION = {
    "alabama": "al", "alaska": "ak", "arizona": "az", "arkansas": "ar", "california": "ca",
    "colorado": "co", "connecticut": "ct", "delaware": "de", "florida": "fl", "georgia": "ga",
    "hawaii": "hi", "idaho": "id", "illinois": "il", "indiana": "in", "iowa": "ia",
    "kansas": "ks", "kentucky": "ky", "louisiana": "la", "maine": "me", "maryland": "md",
    "massachusetts": "ma", "michigan": "mi", "minnesota": "mn", "mississippi": "ms",
    "missouri": "mo", "montana": "mt", "nebraska": "ne", "nevada": "nv", "new hampshire": "nh",
    "new jersey": "nj", "new mexico": "nm", "new york": "ny", "north carolina": "nc",
    "north dakota": "nd", "ohio": "oh", "oklahoma": "ok", "oregon": "or", "pennsylvania": "pa",
    "rhode island": "ri", "south carolina": "sc", "south dakota": "sd", "tennessee": "tn",
    "texas": "tx", "utah": "ut", "vermont": "vt", "virginia": "va", "washington": "wa",
    "west virginia": "wv", "wisconsin": "wi", "wyoming": "wy",
    // Add District of Columbia if present in CSV, e.g., "district of columbia": "dc"
};

/**
 * Parses an age string from the CSV.
 * Handles formats like:
 *  - Canada (New Provincial): "0 years", "1 year", "10 years", "100 years", "110 years and over"
 *  - US (New Normalized): "0", "1", "10", "100" (as plain numbers)
 * @param {string} ageStr - The age string from the CSV.
 * @returns {object|null} An object { min: number, max: number } or null if parsing fails.
 */
const parseAgeValue = (ageStr) => { // Simplified for direct age values or "and over"
    if (!ageStr || typeof ageStr !== 'string') {
        // Allow numbers to be passed directly if CSV parsing already converted them
        if (typeof ageStr === 'number' && !isNaN(ageStr)) {
            return { min: ageStr, max: ageStr };
        }
        return null;
    }

    const ageStringCleaned = String(ageStr).trim();

    // Handle "X years and over" or "100 and over" or "110 years and over"
    if (ageStringCleaned.includes('and over')) {
        const minAge = parseInt(ageStringCleaned.split(' ')[0], 10);
        return isNaN(minAge) ? null : { min: minAge, max: Infinity };
    }

    // Handle formats like "X years" (Canada) or plain numbers (US new)
    const singleAgeMatch = ageStringCleaned.match(/^(\d+)/); // Match leading digits
    if (singleAgeMatch) {
        const singleAge = parseInt(singleAgeMatch[1], 10);
        return isNaN(singleAge) ? null : { min: singleAge, max: singleAge };
    }

    console.warn(`Could not parse age format "${ageStringCleaned}"`);
    return null; // Fallback if no format matches
};


/**
 * Calculates the expected end of life date and retrieves the raw life expectancy years.
 * @param {string} claimantDOB - Date of Birth in 'YYYY-MM-DD' format.
 * @param {string} claimantGender - Gender ('Male', 'Female', or other).
 * @param {string} claimantCountry - Country ('US' or 'Canada').
 * @param {string|null} claimantLocation - Province for Canada, State for US.
 * @returns {Promise<{endDate: string, expectancyYears: number}|null>}
 */
export const calculateEndOfLife = async (claimantDOB, claimantGender, claimantCountry = 'US', claimantLocation = null) => {
    if (!claimantDOB) {
        console.warn("Cannot calculate end of life: Claimant DOB is missing.");
        return null;
    }
    if (!claimantCountry || !LIFE_EXPECTANCY_TABLE_PATHS[claimantCountry]) {
        console.error(`Invalid or missing country provided: '${claimantCountry}'. Cannot proceed.`);
        return null;
    }
    // For US, claimantLocation (state) is preferred but can fallback to national if missing or unmappable.
    // For Canada, claimantLocation (province) is strictly required for provincial CSV.
    if (claimantCountry === 'Canada' && !claimantLocation) {
        console.warn(`Cannot calculate end of life for Canada: Claimant Province is missing.`);
        return null;
    }
    if (claimantCountry === 'US' && !claimantLocation) {
        console.warn(`US Claimant State is missing. Will attempt to use national US data.`);
        // Allow proceeding, will use national US data by default if state-level fails or is skipped.
    }

    try {
        const dobDate = safeParseISO(claimantDOB);
        if (isNaN(dobDate.getTime())) {
             console.error("Invalid Date of Birth provided:", claimantDOB);
             return null;
        }

        const age = differenceInYears(new Date(), dobDate);
        if (age < 0) {
            console.error("Date of Birth is in the future:", claimantDOB);
            return null;
        }

        // Primary attempt: Use state-level normalized CSV for US, or provincial for Canada
        let tablePath = claimantCountry === 'US' ? LIFE_EXPECTANCY_TABLE_PATHS.US.stateLevel : LIFE_EXPECTANCY_TABLE_PATHS.Canada;
        let isUsingNormalizedUSData = claimantCountry === 'US';
        let attemptNationalUSFallback = false;

        let response = await fetch(tablePath);
        let rawCsvText = response.ok ? await response.text() : null;
        let lifeExpectancyYears = null;

        if (response.ok) {
            lifeExpectancyYears = await new Promise((resolveCsvParse) => {
                Papa.parse(rawCsvText, {
                    delimiter: ',',
                    header: false,
                    skipEmptyLines: true,
                    complete: (results) => {
                        if (results.errors.length > 0 || !results.data || results.data.length <= 1) {
                            console.error(`Papaparse errors or no data in ${tablePath}:`, results.errors);
                            resolveCsvParse(null);
                            return;
                        }
                        const dataRows = results.data.slice(1); // Skip header
                        let foundExpectancy = null;

                        if (claimantCountry === 'Canada') {
                            // ... [Canadian logic remains largely the same, ensure it uses 'claimantLocation' for province]
                            // (For brevity, assuming Canadian logic from previous step is correctly placed here)
                            const findCanadianExpectancy = (searchProvince, searchSexCsv) => {
                                for (const row of dataRows) {
                                    if (!Array.isArray(row) || row.length <= CAN_PROV_LIFE_EXPECTANCY_COL) continue;
                                    const provinceInRow = String(row[CAN_PROV_PROVINCE_COL]).trim().toLowerCase();
                                    const sexInRow = String(row[CAN_PROV_SEX_COL]).trim();
                                    const ageStr = String(row[CAN_PROV_AGE_GROUP_COL]).trim();
                                    const ageRange = parseAgeValue(ageStr); // Uses simplified parseAgeValue

                                    if (ageRange && age >= ageRange.min && age <= ageRange.max &&
                                        provinceInRow === searchProvince.toLowerCase() &&
                                        sexInRow === searchSexCsv) {
                                        const expectancyValueStr = row[CAN_PROV_LIFE_EXPECTANCY_COL];
                                        const parsedExpectancy = parseFloat(String(expectancyValueStr).replace(/,/g, ''));
                                        if (!isNaN(parsedExpectancy)) {
                                            return parsedExpectancy;
                                        }
                                    }
                                }
                                return null;
                            };
                            const initialTargetSexCsv = claimantGender === 'Male' ? 'Males' : (claimantGender === 'Female' ? 'Females' : 'Both sexes');
                            foundExpectancy = findCanadianExpectancy(claimantLocation, initialTargetSexCsv);
                            if (foundExpectancy === null && initialTargetSexCsv !== 'Both sexes') {
                                //console.warn(`Canada: Exact sex match not found for Province "${claimantLocation}", Sex "${initialTargetSexCsv}". Falling back to 'Both sexes'.`);
                                foundExpectancy = findCanadianExpectancy(claimantLocation, 'Both sexes');
                            }
                            if (foundExpectancy === null) {
                                //console.warn(`Canada: Province match not found for "${claimantLocation}" (Sex: ${initialTargetSexCsv}). Falling back to 'Canada' as province.`);
                                foundExpectancy = findCanadianExpectancy("Canada", initialTargetSexCsv);
                            }
                            if (foundExpectancy === null && initialTargetSexCsv !== 'Both sexes') {
                                //console.warn(`Canada: No match for Province "${claimantLocation}" or "Canada" with Sex "${initialTargetSexCsv}". Falling back to 'Canada' as province with 'Both sexes'.`);
                                foundExpectancy = findCanadianExpectancy("Canada", 'Both sexes');
                            }
                        } else if (isUsingNormalizedUSData) { // US Normalized State-Level Data
                            const getAbbreviation = (stateName) => {
                                if (!stateName || typeof stateName !== 'string') return null;
                                return US_STATE_NAME_TO_ABBREVIATION[stateName.trim().toLowerCase()] || null;
                            };
                            const stateCodeForSearch = getAbbreviation(claimantLocation);

                            if (!stateCodeForSearch) {
                                console.warn(`US: Could not map full state name "${claimantLocation}" to a 2-letter abbreviation. Will skip state-level CSV and attempt national fallback.`);
                                attemptNationalUSFallback = true; // Signal to use national fallback
                                resolveCsvParse(null); // End this parsing attempt
                                return;
                            }

                            const findUSNormExpectancy = (stateCodeToSearch, sexCsvToSearch) => {
                                for (const row of dataRows) {
                                    if (!Array.isArray(row) || row.length <= US_NORM_LIFE_EXPECTANCY_COL) continue;
                                    const stateInRow = String(row[US_NORM_STATE_COL]).trim().toLowerCase();
                                    const sexInRow = String(row[US_NORM_SEX_COL]).trim();
                                    const ageStr = String(row[US_NORM_AGE_COL]).trim();
                                    const ageRange = parseAgeValue(ageStr); // Uses simplified parseAgeValue

                                    if (ageRange && age >= ageRange.min && age <= ageRange.max &&
                                        stateInRow === stateCodeToSearch.toLowerCase() && // Ensure comparison is with mapped code
                                        sexInRow === sexCsvToSearch) {
                                        const expectancyValueStr = row[US_NORM_LIFE_EXPECTANCY_COL];
                                        const parsedExpectancy = parseFloat(String(expectancyValueStr).replace(/,/g, ''));
                                        if (!isNaN(parsedExpectancy)) {
                                            return parsedExpectancy;
                                        }
                                    }
                                }
                                return null;
                            };
                            const initialTargetSexCsv = claimantGender === 'Male' ? 'Male' : (claimantGender === 'Female' ? 'Female' : 'Total');
                            foundExpectancy = findUSNormExpectancy(stateCodeForSearch, initialTargetSexCsv);
                            if (foundExpectancy === null && initialTargetSexCsv !== 'Total') {
                                //console.warn(`US (Normalized): Exact sex match not found for State "${claimantLocation}" (Code: ${stateCodeForSearch}), Sex "${initialTargetSexCsv}". Falling back to 'Total' sex.`);
                                foundExpectancy = findUSNormExpectancy(stateCodeForSearch, 'Total');
                            }
                            // If still not found after state-level attempts, signal for national fallback
                            if (foundExpectancy === null) {
                                console.warn(`US (Normalized): No data found for State "${claimantLocation}" (Code: ${stateCodeForSearch}). Will attempt national fallback.`);
                                attemptNationalUSFallback = true;
                            }
                        }
                        resolveCsvParse(foundExpectancy);
                    },
                    error: (err) => {
                        console.error(`Papaparse error for ${tablePath}:`, err);
                        resolveCsvParse(null);
                    }
                });
            });
        } else { // Initial fetch failed for state-level or provincial
            console.error(`Failed to fetch initial table: ${tablePath}`);
            if (isUsingNormalizedUSData) attemptNationalUSFallback = true; // If state-level US fetch failed, try national
        }


        // US National Fallback Logic
        if (claimantCountry === 'US' && (attemptNationalUSFallback || lifeExpectancyYears === null)) {
            if (lifeExpectancyYears === null) { // Only log if we didn't find it in state-level AND state was mappable
                 if (!attemptNationalUSFallback) // attemptNationalUSFallback is true if state wasn't mappable or fetch failed
                    console.warn(`US: No specific state-level data found for "${claimantLocation}". Falling back to older national US tables.`);
            }

            isUsingNormalizedUSData = false; // Now using old national tables
            const genderKeyNational = (claimantGender === 'Male' ? 'nationalMale' : (claimantGender === 'Female' ? 'nationalFemale' : 'nationalDefault'));
            tablePath = LIFE_EXPECTANCY_TABLE_PATHS.US[genderKeyNational];

            response = await fetch(tablePath);
            rawCsvText = response.ok ? await response.text() : null;

            if (response.ok) {
                lifeExpectancyYears = await new Promise((resolveNationalCsvParse) => {
                    Papa.parse(rawCsvText, {
                        delimiter: ',',
                        header: false,
                        skipEmptyLines: true,
                        complete: (results) => {
                            if (results.errors.length > 0 || !results.data || results.data.length <= 3) { // Old tables had more header
                                console.error(`Papaparse errors or no data in National US table ${tablePath}:`, results.errors);
                                resolveNationalCsvParse(null);
                                return;
                            }
                            // Logic for OLD US Tables (Table1, Table2, Table3)
                            const skipRowsOldUS = 3;
                            const ageColIndexOldUS = 0;
                            const expectancyColIndexOldUS = 6; // Example: 7th column for Table1.csv
                                                              // Table2 (Male) & Table3 (Female) might have expectancy in a different col, adjust if needed.
                                                              // Assuming col 6 for simplicity here, VERIFY THIS for Table2 & Table3.

                            const dataRowsOldUS = results.data.slice(skipRowsOldUS, results.data.length -1); // Old US tables had a footer
                            let foundNationalExpectancy = null;

                            for (const row of dataRowsOldUS) {
                                if (!Array.isArray(row) || row.length <= expectancyColIndexOldUS) continue;
                                const ageRangeStr = String(row[ageColIndexOldUS]).trim();
                                // IMPORTANT: Old US tables used "X-Y" format, parseAgeValue needs to handle this.
                                // The current parseAgeValue is simplified. We might need to pass 'US_old' context or revert its US part.
                                // For now, assuming parseAgeValue can handle single ages if "X-Y" is not present or correctly parses the first part.
                                // This part needs careful re-evaluation of parseAgeValue for old US tables.
                                // A quick fix for old US "X-Y" format:
                                let ageRange;
                                if (ageRangeStr.includes('-')) {
                                    const parts = ageRangeStr.split('-');
                                    if (parts.length === 2) {
                                        const min = parseInt(parts[0], 10);
                                        const max = parseInt(parts[1], 10);
                                        if (!isNaN(min) && !isNaN(max)) ageRange = {min, max: max-1};
                                    }
                                } else {
                                     ageRange = parseAgeValue(ageRangeStr); // Fallback to existing for single numbers or "X and over"
                                }


                                if (ageRange && age >= ageRange.min && age <= ageRange.max) {
                                    const expectancyValueStr = row[expectancyColIndexOldUS];
                                    const parsedExpectancy = parseFloat(String(expectancyValueStr).replace(/,/g, ''));
                                    if (!isNaN(parsedExpectancy)) {
                                        foundNationalExpectancy = parsedExpectancy;
                                        break;
                                    }
                                }
                            }
                            resolveNationalCsvParse(foundNationalExpectancy);
                        },
                        error: (err) => {
                            console.error(`Papaparse error for National US table ${tablePath}:`, err);
                            resolveNationalCsvParse(null);
                        }
                    });
                });
            } else {
                console.error(`Failed to fetch National US fallback table: ${tablePath}`);
            }
        }


        if (lifeExpectancyYears === null) {
             console.warn(`FINAL: Could not find or parse life expectancy for age ${age} (Country: ${claimantCountry}, Location: ${claimantLocation}, Gender: ${claimantGender}).`);
             return null; // Correctly return null for the async function
        }

        const wholeYears = Math.floor(lifeExpectancyYears);
        const fractionalYear = lifeExpectancyYears - wholeYears;
        const daysInYear = 365.2425;
        const additionalDays = Math.round(fractionalYear * daysInYear);

        const currentDate = new Date();
        let endDate = addYears(currentDate, wholeYears);
        endDate = addDays(endDate, additionalDays);

        const formattedEndDate = format(endDate, 'yyyy-MM-dd');
        return { endDate: formattedEndDate, expectancyYears: lifeExpectancyYears }; // Correctly return the object
    } catch (error) {
        console.error("Error in calculateEndOfLife function:", error);
        return null;
    }
};
